/**
 * The wallet domain — offers, vouchers, gift cards and the derived stats the
 * offers drawer renders.
 *
 * Recovered from the original's module 83467 (the offers domain library) and
 * from the `offers` schema the platform actually stores. Two shapes are in play
 * and they do NOT match:
 *
 * - The DATABASE's `offers` schema (recovered from the POS bundle v1.8.5) has
 *   four pathways (`VOUCHER`/`ITEMS`/`ORDERS`/`POINTS`) and seven reward types,
 *   but its pathway/reward sub-documents carry only the older fields.
 * - The CURRENT web build reads newer ones — `pathway_to_unlock_type_vouchers`,
 *   `reward_voucher`, `reward_gift_cards`, `reward_cashback`,
 *   `max_uses_per_customer`, `max_claims`, `category_ids`,
 *   `min/max_same_item_quantity`, `max_discount_amount`.
 *
 * Both are modelled here, with the newer members optional, so a document from
 * either generation type-checks. Nothing in this deployment exercises it yet:
 * `offers`, `vouchers` and `gift_cards` are all EMPTY collections, database
 * wide (verified 2026-08-14). That is tenant data, not code.
 */

/** `OFFER_REWARD_TYPE_ENUM` — the platform's own seven, verbatim. */
export const OFFER_REWARD_TYPE_ENUM = {
  DISCOUNT: "DISCOUNT",
  DISCOUNT_ITEMS: "DISCOUNT_ITEMS",
  POINTS: "POINTS",
  CASHBACK: "CASHBACK",
  GIFT_CARDS: "GIFT_CARDS",
  ITEMS: "ITEMS",
  VOUCHER: "VOUCHER",
} as const;

export type OfferRewardType =
  (typeof OFFER_REWARD_TYPE_ENUM)[keyof typeof OFFER_REWARD_TYPE_ENUM];

/** `PATHWAY_TO_UNLOCK_TYPE_ENUM` — exactly four. */
export const PATHWAY_TO_UNLOCK_TYPE_ENUM = {
  VOUCHER: "VOUCHER",
  ITEMS: "ITEMS",
  ORDERS: "ORDERS",
  POINTS: "POINTS",
} as const;

export type PathwayToUnlockType =
  (typeof PATHWAY_TO_UNLOCK_TYPE_ENUM)[keyof typeof PATHWAY_TO_UNLOCK_TYPE_ENUM];

/**
 * The two buckets the redemption-count endpoint answers in. This enum is the
 * WEB build's own — it has no counterpart in the shared database enums, because
 * no `customer_redemptions` collection exists in this deployment.
 */
export const CUSTOMER_REDEMPTION_TYPE_ENUM = {
  ASSIGNED: "ASSIGNED",
  USED: "USED",
} as const;

export type CustomerRedemptionType =
  (typeof CUSTOMER_REDEMPTION_TYPE_ENUM)[keyof typeof CUSTOMER_REDEMPTION_TYPE_ENUM];

/** `{ ASSIGNED: { [id]: n }, USED: { [id]: n } }`, or `null` before it loads. */
export type CustomerRedemptionCounts = Record<
  CustomerRedemptionType,
  Record<string, number>
> | null;

export interface OfferPathwayItems {
  num_of_items: number;
  total_amount: number;
  excluded_refs?: string[];
  exclusive_refs?: string[];
  item_ids: string[];
  category_ids?: string[];
  max_uses_per_customer?: number | null;
  max_claims?: number | null;
  min_same_item_quantity?: number | null;
  max_same_item_quantity?: number | null;
}

export interface OfferPathwayOrders {
  min_num_of_orders: number;
  max_num_of_orders?: number | null;
  total_amount: number;
  max_amount?: number | null;
  max_uses_per_customer?: number | null;
  max_claims?: number | null;
  orders_between_unlocks?: number | null;
  scope?: string | null;
}

export interface OfferPathwayPoints {
  num_of_points: number;
  total_amount: number;
  max_uses_per_customer?: number | null;
  max_claims?: number | null;
}

export interface OfferPathwayVouchers {
  voucher_ids: string[];
  total_amount?: number;
  max_amount?: number | null;
  max_uses_per_customer?: number | null;
  max_claims?: number | null;
}

export interface OfferRewardItems {
  num_of_items: number;
  excluded_refs?: string[];
  exclusive_refs?: string[];
  item_ids: string[];
  allocation?: { quantity: number; item_ids: string[] }[] | null;
}

export interface OfferRewardDiscount {
  discount_value: number;
  is_discount_percentage?: boolean;
  excluded_refs?: string[];
  max_discount_amount?: number | null;
  max_discount_percentage?: number | null;
}

export interface OfferRewardDiscountItems extends OfferRewardItems {
  discount_value: number;
  is_discount_percentage?: boolean;
}

export interface OfferRewardPoints {
  num_of_points: number;
  once_per_order?: boolean;
}

export interface OfferRewardCashback {
  cashback_value: number;
  is_cashback_percentage?: boolean;
}

/** Both reward maps are keyed by id, so the engine reads `Object.keys`. */
export interface OfferRewardVoucher {
  voucher_ids: Record<string, number>;
}

export interface OfferRewardGiftCards {
  gift_card_ids: Record<string, number>;
}

export interface Offer {
  _id: string;
  brand_id?: string;
  tenants?: string[];
  image_url?: string;
  name: string;
  description?: string;
  active: boolean;
  included_platforms: string[];
  sort_id: number;
  offer_special_hours?: unknown[] | null;
  auto_apply?: boolean;
  pathway_to_unlock_type: PathwayToUnlockType;
  pathway_to_unlock_type_items?: OfferPathwayItems | null;
  pathway_to_unlock_type_orders?: OfferPathwayOrders | null;
  pathway_to_unlock_type_points?: OfferPathwayPoints | null;
  pathway_to_unlock_type_vouchers?: OfferPathwayVouchers | null;
  reward_type: OfferRewardType;
  reward_items?: OfferRewardItems | null;
  reward_discount?: OfferRewardDiscount | null;
  reward_discount_items?: OfferRewardDiscountItems | null;
  reward_points?: OfferRewardPoints | null;
  reward_cashback?: OfferRewardCashback | null;
  reward_voucher?: OfferRewardVoucher | null;
  reward_gift_cards?: OfferRewardGiftCards | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * A voucher. No recovered schema exists for this collection — the POS bundle
 * never carried one — so the fields are those the storefront reads.
 */
export interface Voucher {
  _id: string;
  name: string;
  code: string;
  active: boolean;
  expire_at?: string | null;
  max_uses?: number | null;
  used_count?: number;
  max_assignments?: number | null;
  assigned_count?: number;
  max_assignments_per_customer?: number | null;
  max_uses_per_customer?: number | null;
}

export interface GiftCard {
  _id: string;
  name: string;
  code: string;
  active: boolean;
  expire_at?: string | null;
  balance?: number;
  reward_type?: OfferRewardType;
  reward_discount?: OfferRewardDiscount | null;
  reward_items?: OfferRewardItems | null;
  reward_points?: OfferRewardPoints | null;
  reward_cashback?: OfferRewardCashback | null;
  max_assignments?: number | null;
  assigned_count?: number;
  max_assignments_per_customer?: number | null;
  max_uses_per_customer?: number | null;
}

/** The label block `buildRewardPreview` (`eu`) produces for a card. */
export interface OfferRewardPreview {
  type: OfferRewardType;
  label: string;
  discount_value?: number;
  is_percentage?: boolean;
  max_discount_amount?: number | null;
  max_discount_percentage?: number | null;
  free_items_count?: number;
  points_value?: number;
  cashback_value?: number;
  gift_card_value?: number;
}

/** What the offer card renders. Produced by the original's `getOffersWithStats`. */
export interface OfferStats {
  progress: number;
  message: string;
  invalid_offer: boolean;
  redeemed_times: number;
  claimable: boolean;
  applicable_quantity: number;
  eligible_item_ids: string[];
  eligible_category_ids: string[];
  reward_preview: OfferRewardPreview | null;
  action_hint: string | null;
  auto_apply: boolean;
}

export interface OfferWithStats {
  offer: Offer;
  stats: OfferStats;
}

/** `getOfferUsageLimitMeta`'s return. */
export interface OfferUsageLimit {
  maxUsesPerCustomer: number;
  usageKey: string;
}

/** One entry of `cart.data.wallet_redemptions.gift_cards`. */
export interface WalletGiftCardRedemption {
  gift_card_id: string;
  redeemed_times: number;
  mystery_gift_type?: string | null;
}

export interface WalletRedemptions {
  cashback_amount?: number;
  gift_cards?: WalletGiftCardRedemption[] | null;
}

/**
 * The discount block the cart carries once something has been applied. Only the
 * members the wallet reads are modelled; order pricing is server-authoritative
 * in this reconstruction (a documented deviation), so the rest never reaches
 * the browser.
 */
export interface OrderDiscountDetails {
  total_discount?: number;
  loyalty_discount_id?: string;
  loyalty_reference_number?: string;
  voucher_discount_id?: string;
  voucher_discount_name?: string;
  voucher_discount?: number;
  is_voucher_discount_percentage?: boolean;
  voucher_discount_reason?: string;
  voucher_reference_number?: string;
}

/** The 2s overlay's payload. */
export interface RedemptionFeedback {
  message: string;
  detail?: string | null;
}
